/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Badge,
    Button,
    Divider,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    PositioningVirtualElement,
    Spinner,
    Text,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import {
    DeleteRegular,
    EditRegular,
    MoneyOffRegular,
    MoneyRegular,
    MoreHorizontal20Regular,
    PlayFilled,
    ReceiptRegular,
    StopFilled,
} from '@fluentui/react-icons';
import { Column, Paths, Row } from '../common';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog';
import { AddEditTimeTrackEntryDialog } from '../dialogs/AddEditTimeTrackEntryDialog';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { useAccess } from '../../auth/useAccess';
import { TimeTrackingEntry, UserAccount } from '../../data/types';
import { useActiveTimeEntryService } from '../../data/services/useActiveTimeEntryService';
import { useActiveTimer } from '../../data/services/useActiveTimer';
import { useTimeTrackingService } from '../../data/services/useTimeTrackingService';
import { useUtilities } from '../../utilities/useUtilities';
import { useCaseInvoiceState } from '../../redux/reducers/caseInvoiceReducer';
import { appTokens } from '../common/Styles/appTokens';
import { CaseTabs } from '../pages/Tabs/CaseTabs';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { TimeTrackingTimerBarControl } from './TimeTrackingTimerBarControl';

const useStyles = makeStyles({
    clickable: {
        cursor: 'pointer',
    },
    invoiceBadge: {
        cursor: 'pointer',
    },
});

type TimeTrackingEntryControlProps = {
    entry: TimeTrackingEntry;
    openEntry?: TimeTrackingEntry | null;
    onModifyTimeEntry?: (entry?: TimeTrackingEntry) => Promise<void>;
    onDeleteTimeEntry?: (entry?: TimeTrackingEntry) => Promise<void>;
    className?: string;
    showUserInfo?: boolean;
};
export const TimeTrackingEntryControl = (props: TimeTrackingEntryControlProps) => {
    const { className, entry, onModifyTimeEntry, onDeleteTimeEntry, showUserInfo = false } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const invoiceState = useCaseInvoiceState();
    const { accountUsers, user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const utils = useUtilities();
    const { activeTimeEntry } = useActiveTimeEntryService();
    const { hoursForEntry } = useActiveTimer();
    const timeEntryService = useTimeTrackingService();
    const navTo = useNavigate();
    const mediaSizes = useMediaSizes();

    //State
    const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [contextMenuTarget, setContextMenuTarget] = useState<PositioningVirtualElement | null>(null);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const controlClasses = mergeClasses(commonStyles.utility.withHiddenChildren, styles.clickable, className);

    //Logic
    const isOwnEntry = () => entry?.userId == user?.userId;
    const isActiveEntry = () => {
        return activeTimeEntry?.entryId == entry?.entryId;
    };
    const isInvoiced = () => !!entry?.invoiceId;
    const isEntryToday = () => {
        return !!entry?.date && utils.date.isToday(new Date(entry.date));
    };

    //Access
    const canEditTimeEntry = () => {
        return !isInvoiced() && isOwnEntry();
    };
    const canDeleteTimeEntry = () => {
        return !isInvoiced() && (isOwnEntry() || !!access.account.isAccountAdmin());
    };
    const canStartStopTimer = () => {
        return !isInvoiced() && isOwnEntry() && isEntryToday();
    };
    const getUsersName = (userId?: string) => {
        return accountUsers.find((x) => x.userId == userId)?.name || userId;
    };
    const getInvoiceNumber = (invoiceId?: string) => {
        return invoiceState.invoices.find((x) => x.invoiceId == invoiceId)?.invoiceNumber || invoiceId;
    };
    const getEntryDate = () => {
        return !!entry?.date ? new Date(entry.date).toLocaleDateString() : '';
    };

    //UI Events
    const onClickStartTimer = async (e: any) => {
        e.stopPropagation();

        await timeEntryService.startTimerAsync(entry);
    };
    const onClickStopTimer = async (e: any) => {
        e.stopPropagation();

        await timeEntryService.stopTimerAsync(entry);
    };
    const onSubmit = async (updatedEntry?: TimeTrackingEntry) => {
        !!onModifyTimeEntry && onModifyTimeEntry(updatedEntry);
    };
    const onConfirmDeleteEntry = (e: any) => {
        e.stopPropagation();

        !!onDeleteTimeEntry && onDeleteTimeEntry(entry);
    };
    const navToInvoice = (e: any, invoiceId?: string) => {
        e.stopPropagation();

        if (!!invoiceId) {
            navTo(Paths.viewEditCase(entry.caseId!), { state: { tab: CaseTabs.invoices, invoiceId } });
        }
    };
    const onDoubleClickCard = (e: any) => {
        e.stopPropagation();

        if (!entry?.timerOn && canEditTimeEntry()) {
            setIsEditOpen(true);
        }
    };
    const onContextMenuCard = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const { clientX, clientY } = e;
        // Anchor the menu to the pointer location instead of the trigger button.
        setContextMenuTarget({ getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0) });
        setIsMenuOpen(true);
    };
    const onMenuOpenChange = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);

        if (!isOpen) {
            setContextMenuTarget(null);
        }
    };

    const hours = hoursForEntry(entry);

    return (
        <>
            <Column
                shadowSmall
                className={controlClasses}
                subtleBorder
                padding='pl-2 pr-3 py-2'
                height='fit-content'
                onDoubleClick={onDoubleClickCard}
                onContextMenu={onContextMenuCard}
                borderLeftStyle={appTokens.timeEntryControl.borderLeftStyle(entry?.isBillable)}
                borderRadius={appTokens.timeEntryControl.borderRadius}
                backgroundColor={appTokens.timeEntryControl.backgroundColor(entry?.invoiceId)}
                gap='small'
            >
                <Row noWrap gap='medium'>
                    <Column flex='1' vAlign='middle'>
                        <Text size={200} weight='regular'>
                            TIME ENTRY - {getEntryDate()}
                        </Text>
                    </Column>
                    <Column flex='0 0 auto' align='right' vAlign='middle'>
                        <TimeTrackingEntryControlMenu
                            entry={entry}
                            open={isMenuOpen}
                            onOpenChange={onMenuOpenChange}
                            positioningTarget={contextMenuTarget}
                            isActiveEntry={isActiveEntry()}
                            hasActiveTimeEntry={!!activeTimeEntry}
                            onStartTimer={onClickStartTimer}
                            onStopTimer={onClickStopTimer}
                            onEdit={() => setIsEditOpen(true)}
                            onDelete={onConfirmDeleteEntry}
                            canEdit={canEditTimeEntry()}
                            canDelete={canDeleteTimeEntry()}
                            canStartStop={canStartStopTimer()}
                        />
                    </Column>
                </Row>

                <Row noWrap gap='medium' padding='pb-2'>
                    <Column noGap flex='1 1 auto' vAlign='middle' align='left' overflowX='hidden' minWidth='0px'>
                        <Text size={mediaSizes.isMobile ? 400 : 500} weight='semibold'>
                            {entry?.description?.substring(0, 150)}
                            {entry?.description && entry.description.length > 150 ? '...' : ''}
                        </Text>
                        {!!showUserInfo && (
                            <Row gap='small' noWrap>
                                <Column vAlign='middle' padding='pt-0' width='auto'>
                                    <Text weight='semibold' size={200}>
                                        User:
                                    </Text>
                                </Column>
                                <Column vAlign='top' align='left' minWidth='0px'>
                                    <Text size={200}>{getUsersName(entry?.userId)}</Text>
                                </Column>
                            </Row>
                        )}
                    </Column>
                    <Column flex='0 0 auto' vAlign='middle' align='right'>
                        <Text size={mediaSizes.isMobile ? 400 : 500} weight='semibold'>
                            {Number(hours).toFixed(2)} hrs
                        </Text>
                    </Column>
                </Row>

                <Column flex='1 1 auto' className={!isActiveEntry() ? 'hidden' : ''} align='right'>
                    {!!isEntryToday() && user.userId === entry?.userId && (
                        <TimeTrackingTimerBarControl timeEntry={entry} size='small' />
                    )}
                </Column>

                <Column flex='1' align='left' vAlign='bottom'>
                    <Row width='auto' gap='medium'>
                        {!!entry?.isBillable ? (
                            <Badge
                                appearance='tint'
                                color='success'
                                size='small'
                                icon={<MoneyRegular />}
                                style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                            >
                                BILLABLE
                            </Badge>
                        ) : (
                            <Badge
                                appearance='tint'
                                color='brand'
                                size='small'
                                icon={<MoneyOffRegular />}
                                style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                            >
                                Non-Billable
                            </Badge>
                        )}
                        {isInvoiced() && (
                            <Badge
                                appearance='tint'
                                color='success'
                                size='small'
                                icon={<ReceiptRegular />}
                                className={styles.invoiceBadge}
                                style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                                onClick={(e: any) => navToInvoice(e, entry?.invoiceId)}
                            >
                                Invoiced - {getInvoiceNumber(entry?.invoiceId)}
                            </Badge>
                        )}
                    </Row>
                </Column>
            </Column>

            {canEditTimeEntry() && (
                <AddEditTimeTrackEntryDialog
                    timeTrackEntryToEdit={entry}
                    caseId={entry?.caseId}
                    entryDate={entry?.date}
                    onSubmit={onSubmit}
                    open={isEditOpen}
                    onOpenChange={(isOpen) => setIsEditOpen(isOpen)}
                />
            )}
        </>
    );
};

interface TimeTrackingEntryControlMenuProps {
    entry: TimeTrackingEntry;
    isActiveEntry?: boolean;
    hasActiveTimeEntry?: boolean;
    onStartTimer?: (e: any) => Promise<void>;
    onStopTimer?: (e: any) => Promise<void>;
    onEdit?: () => void;
    onDelete?: (e: any) => void;
    canEdit?: boolean;
    canDelete?: boolean;
    canStartStop?: boolean;
    className?: string;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    positioningTarget?: PositioningVirtualElement | null;
}
const TimeTrackingEntryControlMenu = ({
    entry,
    isActiveEntry,
    hasActiveTimeEntry,
    open,
    onOpenChange,
    positioningTarget,
    onStartTimer,
    onStopTimer,
    onEdit,
    onDelete,
    canEdit,
    canDelete,
    canStartStop,
    className,
}: TimeTrackingEntryControlMenuProps) => {
    const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

    // Timers are task-based, so ad-hoc entries have no start/stop.
    const showTimerActions = !!canStartStop && !!entry?.taskId;
    const showEdit = !!canEdit && !entry?.timerOn;

    if (!showTimerActions && !showEdit && !canDelete) return null;
    return (
        <>
            <Menu
                open={open}
                onOpenChange={(_, data) => onOpenChange?.(data.open)}
                positioning={positioningTarget ? { target: positioningTarget } : undefined}
            >
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        icon={<MoreHorizontal20Regular />}
                        size='small'
                        appearance='secondary'
                        className={className}
                        aria-label='Actions'
                        onClick={(e: any) => e.stopPropagation()}
                        data-testid='time-entry-actions-menu-trigger'
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {showTimerActions && (
                            <MenuItem
                                icon={
                                    isActiveEntry ? (
                                        <Spinner size='extra-tiny' />
                                    ) : (
                                        <PlayFilled
                                            color={
                                                hasActiveTimeEntry
                                                    ? tokens.colorNeutralForegroundDisabled
                                                    : tokens.colorStatusSuccessForeground1
                                            }
                                        />
                                    )
                                }
                                disabled={hasActiveTimeEntry}
                                onClick={onStartTimer}
                            >
                                Start Time
                            </MenuItem>
                        )}

                        {showTimerActions && (
                            <MenuItem
                                icon={
                                    <StopFilled
                                        color={
                                            !isActiveEntry
                                                ? tokens.colorNeutralForegroundDisabled
                                                : tokens.colorStatusDangerForeground1
                                        }
                                    />
                                }
                                disabled={!isActiveEntry}
                                onClick={onStopTimer}
                            >
                                Stop Time
                            </MenuItem>
                        )}

                        {showTimerActions && (showEdit || !!canDelete) && <Divider />}

                        {showEdit && (
                            <MenuItem icon={<EditRegular />} onClick={onEdit} data-testid='time-entry-edit-button'>
                                Edit Time Entry
                            </MenuItem>
                        )}

                        {!!canDelete && (
                            <MenuItem
                                icon={<DeleteRegular />}
                                onClick={() => setIsDeleteOpen(true)}
                                data-testid='time-entry-delete-button'
                            >
                                Delete Time Entry
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>

            {/* Rendered outside the Menu so closing the menu popover doesn't unmount the dialog. */}
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text='Are you sure you want to delete this time tracking entry?'
                onConfirm={onDelete}
                open={isDeleteOpen}
                onOpenChange={setIsDeleteOpen}
            />
        </>
    );
};
